import React from 'react';
import { Container } from '@ui';
import { Select, TextField } from '@ui/inputs';
import { docTypes } from '@constants';

// One document-type picker, shared by the trigger, `filter`, `related_filter` and `screen`.
//
// The options come from the app-wide `docTypes` constant, which is the list an operator recognises.
// It is NOT the whole vocabulary the scoring engine indexes, and the gap is large enough to matter:
// of the 23 document types the shipped production specs actually use, only 7 appear in that
// constant. The proxy filings (`DEF 14A`, `DEFA14A`, `PREC14A`, …), `S-1`, `F-1` and `4` are all
// real, all in use, and all absent from it.
//
// So the control is a dropdown with two escapes, and both are load-bearing:
//
//   1. A value already in the spec that is not in the constant is added to the option list as
//      itself, so opening one of those specs shows the real type rather than an empty box.
//   2. "Other…" reveals a free-text field, so a type outside the constant can still be authored.
//
// Nothing here validates the name. A type the engine does not index is not an error — the spec
// loads cleanly and simply never selects anything.

const OTHER = '__other__';

export const KNOWN_DOC_TYPES = Array.from(new Set(Object.values(docTypes))).sort((a, b) => a.localeCompare(b));

export const DocTypeSelect = ({ value, onChange, label, ariaLabel, invalid, hint }) => {
  const current = value || '';
  const isKnown = KNOWN_DOC_TYPES.indexOf(current) > -1;

  // Free-text mode is entered explicitly, and left as soon as a listed type is picked. An unknown
  // value on its own does not force it — that value is offered as an option instead.
  const [wantsCustom, setWantsCustom] = React.useState(false);

  const options = KNOWN_DOC_TYPES.map((type) => ({ value: type, label: type }))
    .concat(current && !isKnown ? [{ value: current, label: current }] : [])
    .concat([{ value: OTHER, label: 'Other…' }]);

  return (
    <div>
      <Select
        label={label}
        aria-label={ariaLabel}
        value={wantsCustom ? OTHER : current}
        placeholder={current ? undefined : 'Choose a document type…'}
        options={options}
        invalid={invalid}
        hint={wantsCustom ? undefined : hint}
        onChange={(next) => {
          if (next === OTHER) {
            setWantsCustom(true);
            return;
          }
          setWantsCustom(false);
          onChange(next);
        }}
      />
      {wantsCustom && (
        <Container top={8}>
          <TextField
            aria-label={ariaLabel ? `${ariaLabel} — custom` : 'Custom document type'}
            value={current}
            onChange={onChange}
            placeholder="Exactly as the engine indexes it"
            invalid={invalid}
            hint={hint || 'Not checked. A name the engine does not index loads cleanly and never fires.'}
          />
        </Container>
      )}
    </div>
  );
};
